(() => {
  const runtime = window.__eightFaqIndexV2 || { instances: new Map(), lifecycleBound: false };
  window.__eightFaqIndexV2 = runtime;

  const categoryOrder = ['product', 'launch', 'orders', 'privacy', 'support', 'community'];
  const categoryLabels = {
    featured: 'Featured',
    all: 'All answers',
    product: 'Product',
    launch: 'Launch',
    orders: 'Orders',
    privacy: 'Privacy',
    support: 'Support',
    community: 'Community',
  };
  const categorySummaries = {
    product: 'Product basics, capture, fit, connectivity, lenses, storage, and planned specifications.',
    launch: 'Availability, timing, pricing, the waitlist, Kickstarter, and direct pre-orders.',
    orders: 'Payment, shipping, taxes, address changes, and order-specific guidance.',
    privacy: 'Responsible capture, consent, storage, account controls, and local rules.',
    support: 'Warranty, returns, repairs, response times, and official updates.',
    community: 'Creators, affiliates, collaborations, and professional use.',
  };
  const routeFixes = {
    '/pages/specifications': '/pages/specs',
    '/pages/8ight-studio': '/pages/app',
    '/pages/8ight-pro-kickstarter': '/pages/kickstarter-landing',
  };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const normalize = (value = '') => String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const displayText = (value = '') => escapeHtml(
    String(value).replace(/([A-Za-z0-9])-(?=[A-Za-z0-9])/g, '$1\u2011'),
  );

  const itemCategory = (item) => {
    const category = normalize(item.category);
    return categoryOrder.includes(category) ? category : 'support';
  };

  const fixedHref = (href = '') => routeFixes[href] || href;

  const initFaq = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.faqEnhanced === 'true') return;

    const dataNode = root.querySelector('[data-faq-data]');
    const search = root.querySelector('[data-search]');
    const searchForm = root.querySelector('[data-search-form]');
    const clearButton = root.querySelector('[data-clear]');
    const answerList = root.querySelector('[data-answer-list]');
    const empty = root.querySelector('[data-empty]');
    const resultTitle = root.querySelector('[data-result-title]');
    const resultEyebrow = root.querySelector('[data-result-eyebrow]');
    const resultSummary = root.querySelector('[data-result-summary]');
    const resultCount = root.querySelector('[data-result-count]');
    const topicButtons = [...root.querySelectorAll('[data-category]')];
    const resetButton = root.querySelector('[data-reset]');
    const totalCount = root.querySelector('[data-total-count]');
    const answersSection = root.querySelector('[data-answers-section]');

    if (!dataNode || !search || !searchForm || !clearButton || !answerList || !empty
      || !resultTitle || !resultEyebrow || !resultSummary || !resultCount || !answersSection) return;

    let items;
    try {
      items = JSON.parse(dataNode.textContent.trim());
      if (!Array.isArray(items) || items.length !== 60) throw new Error('Expected 60 FAQ answers.');
    } catch (error) {
      console.warn('FAQ enhancement was not enabled; the complete static directory remains available.', error);
      answerList.setAttribute('aria-busy', 'false');
      return;
    }

    const state = { items, category: 'featured', query: '' };
    const sectionId = root.dataset.sectionId || 'main';
    const previousInstance = runtime.instances.get(sectionId);
    if (previousInstance) {
      document.removeEventListener('keydown', previousInstance.keyHandler);
      runtime.instances.delete(sectionId);
    }

    const currentHashId = () => {
      try {
        return decodeURIComponent(window.location.hash.slice(1));
      } catch {
        return '';
      }
    };

    const searchText = (item) => normalize([
      item.category,
      item.subgroup,
      item.question,
      item.answer,
      item.preview,
      ...(Array.isArray(item.tags) ? item.tags : []),
    ].join(' '));

    const renderLinks = (links) => {
      if (!Array.isArray(links) || links.length === 0) return '';
      return `<div class="answer-item__links">${links.map((link) => (
        `<a href="${escapeHtml(fixedHref(link.href))}">${displayText(link.label)}</a>`
      )).join('')}</div>`;
    };

    const renderItem = (item, index) => {
      const category = itemCategory(item);
      const subgroup = item.subgroup || categoryLabels[category];
      return [
        `<details class="answer-item" id="${escapeHtml(item.id || `answer-${index + 1}`)}" name="faq-accordion-${escapeHtml(sectionId)}">`,
        '<summary>',
        `<span class="answer-item__index">${String(index + 1).padStart(2, '0')}</span>`,
        `<span class="answer-item__question">${displayText(item.question)}</span>`,
        '<span class="answer-item__plus" aria-hidden="true"></span>',
        '</summary>',
        '<div class="answer-item__body"><div>',
        '<dl class="answer-item__meta">',
        `<div><dt>Topic</dt><dd>${displayText(categoryLabels[category])}</dd></div>`,
        `<div><dt>Section</dt><dd>${displayText(subgroup)}</dd></div>`,
        '</dl>',
        `<p class="answer-item__answer">${displayText(item.answer)}</p>`,
        renderLinks(item.links),
        '</div></div>',
        '</details>',
      ].join('');
    };

    const updateUrl = () => {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('view');
      if (state.query) {
        url.searchParams.set('q', state.query);
        url.searchParams.delete('topic');
      } else {
        url.searchParams.delete('q');
        if (state.category === 'featured') url.searchParams.delete('topic');
        else url.searchParams.set('topic', state.category);
      }
      window.history.replaceState({}, '', url);
    };

    const updateTopicState = () => {
      topicButtons.forEach((button) => {
        const active = button.dataset.category === state.category && !state.query;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    const visibleItems = () => {
      const query = normalize(state.query);
      if (query) return state.items.filter((item) => searchText(item).includes(query));
      if (state.category === 'featured') return state.items.filter((item) => item.featured).slice(0, 6);
      if (state.category === 'all') return state.items;
      return state.items.filter((item) => itemCategory(item) === state.category);
    };

    const updateCopy = (visible) => {
      if (state.query) {
        resultEyebrow.textContent = 'Search results';
        resultTitle.textContent = visible.length ? 'Matching answers.' : 'Nothing matched.';
        resultSummary.textContent = visible.length
          ? `Results for \u201c${state.query}\u201d.`
          : 'Try one or two simpler words, or browse a topic.';
        resultCount.textContent = `${visible.length} ${visible.length === 1 ? 'answer' : 'answers'} found`;
      } else if (state.category === 'featured') {
        resultEyebrow.textContent = 'Start here';
        resultTitle.textContent = 'Most asked questions.';
        resultSummary.textContent = 'Six answers that cover the questions people ask first.';
        resultCount.textContent = `${visible.length} featured answers shown`;
      } else if (state.category === 'all') {
        resultEyebrow.textContent = 'Complete directory';
        resultTitle.textContent = 'All answers.';
        resultSummary.textContent = 'Every published answer, ordered by what people need first.';
        resultCount.textContent = `${visible.length} answers shown`;
      } else {
        const label = categoryLabels[state.category];
        resultEyebrow.textContent = 'Selected topic';
        resultTitle.textContent = `${label} answers.`;
        resultSummary.textContent = categorySummaries[state.category];
        resultCount.textContent = `${visible.length} ${visible.length === 1 ? 'answer' : 'answers'} shown`;
      }
    };

    const scrollToAnswers = () => answersSection.scrollIntoView({
      behavior: reduceMotion.matches ? 'auto' : 'smooth',
      block: 'start',
    });

    const render = ({ focusTitle = false } = {}) => {
      const visible = visibleItems();
      updateCopy(visible);
      updateTopicState();
      clearButton.hidden = !state.query;
      empty.hidden = visible.length !== 0;
      answerList.hidden = visible.length === 0;
      const visibleIds = new Set(visible.map((item) => item.id));
      answerList.querySelectorAll('.answer-item').forEach((details) => {
        const isVisible = visibleIds.has(details.id);
        details.hidden = !isVisible;
        if (!isVisible) details.open = false;
      });
      answerList.setAttribute('aria-busy', 'false');
      updateUrl();
      if (focusTitle) {
        resultTitle.focus({ preventScroll: true });
        scrollToAnswers();
      }
    };

    const updateCounts = () => {
      if (totalCount) totalCount.textContent = String(state.items.length).padStart(2, '0');
      const totals = {
        featured: state.items.filter((item) => item.featured).slice(0, 6).length,
        all: state.items.length,
      };
      categoryOrder.forEach((category) => {
        totals[category] = state.items.filter((item) => itemCategory(item) === category).length;
      });
      Object.entries(totals).forEach(([category, count]) => {
        const node = root.querySelector(`[data-count="${category}"]`);
        if (!node) return;
        node.textContent = String(count).padStart(2, '0');
        if (category === 'featured' || category === 'all') {
          const label = category === 'featured' ? 'Featured' : 'All';
          node.closest('button')?.setAttribute('aria-label', `${label}, ${count} answers`);
        }
      });
    };

    const setCategory = (category, focusTitle = true) => {
      state.category = categoryLabels[category] ? category : 'featured';
      state.query = '';
      search.value = '';
      render({ focusTitle });
    };

    searchForm.addEventListener('submit', (event) => event.preventDefault());
    let searchTimer = 0;
    search.addEventListener('input', () => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => {
        state.query = search.value.trim();
        state.category = 'featured';
        render();
      }, 90);
    });
    clearButton.addEventListener('click', () => {
      search.value = '';
      state.query = '';
      state.category = 'featured';
      render();
      search.focus();
    });
    resetButton?.addEventListener('click', () => {
      search.value = '';
      state.query = '';
      state.category = 'featured';
      render();
      search.focus();
    });
    topicButtons.forEach((button) => {
      button.addEventListener('click', () => setCategory(button.dataset.category));
    });

    answerList.addEventListener('toggle', (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (details.open) {
        answerList.querySelectorAll('details[open]').forEach((item) => {
          if (item !== details) item.open = false;
        });
        if (details.id) {
          const url = new URL(window.location.href);
          url.hash = details.id;
          window.history.replaceState({}, '', url);
        }
      } else if (details.id && currentHashId() === details.id) {
        const url = new URL(window.location.href);
        url.hash = '';
        window.history.replaceState({}, '', url);
      }
    }, true);

    const keyHandler = (event) => {
      const target = event.target;
      const typing = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target?.isContentEditable;
      if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        search.focus();
      }
    };
    document.addEventListener('keydown', keyHandler);
    runtime.instances.set(sectionId, { root, keyHandler });

    state.items.sort((a, b) => Number(a.priority || 999) - Number(b.priority || 999)
      || String(a.question).localeCompare(String(b.question)));
    const initialHash = currentHashId();
    const params = new URLSearchParams(window.location.search);
    const topic = normalize(params.get('topic'));
    state.category = params.get('view') === 'all'
      ? 'all'
      : (categoryLabels[topic] ? topic : 'featured');
    state.query = (params.get('q') || '').trim();
    search.value = state.query;

    // Keep the complete schema-backed directory in the hydrated DOM and filter by visibility.
    answerList.innerHTML = state.items.map(renderItem).join('');

    root.dataset.faqEnhanced = 'true';
    root.dataset.faqReady = 'true';
    updateCounts();
    render();

    if (initialHash) {
      const requestedItem = state.items.find((item) => item.id === initialHash);
      if (requestedItem) {
        let requestedDetails = root.querySelector(`#${CSS.escape(initialHash)}`);
        if (!(requestedDetails instanceof HTMLDetailsElement) || requestedDetails.hidden) {
          state.category = itemCategory(requestedItem);
          state.query = '';
          render();
          requestedDetails = root.querySelector(`#${CSS.escape(initialHash)}`);
        }
        if (requestedDetails instanceof HTMLDetailsElement) {
          requestedDetails.open = true;
          const url = new URL(window.location.href);
          url.hash = initialHash;
          window.history.replaceState({}, '', url);
          requestedDetails.querySelector('summary')?.focus({ preventScroll: true });
          requestedDetails.scrollIntoView({
            behavior: reduceMotion.matches ? 'auto' : 'smooth',
            block: 'center',
          });
        }
      }
    }
  };

  const boot = (scope = document) => {
    if (scope.matches?.('[data-faq-index]')) initFaq(scope);
    scope.querySelectorAll?.('[data-faq-index]').forEach(initFaq);
  };

  const unload = (scope) => {
    const root = scope.matches?.('[data-faq-index]') ? scope : scope.querySelector?.('[data-faq-index]');
    const sectionId = root?.dataset.sectionId;
    if (!sectionId) return;
    const instance = runtime.instances.get(sectionId);
    if (!instance) return;
    document.removeEventListener('keydown', instance.keyHandler);
    runtime.instances.delete(sectionId);
  };

  boot();
  if (!runtime.lifecycleBound) {
    document.addEventListener('shopify:section:load', (event) => boot(event.target));
    document.addEventListener('shopify:section:unload', (event) => unload(event.target));
    runtime.lifecycleBound = true;
  }
})();


